import * as React from 'react';
import { RightArrowIcon } from '../../common/Icons';
import { NameRequirementManager } from '../../common/requirementManagers/NameRequirementManager';
import {
    UniqueNameValidator,
    LowercaseLettersNumbersHyphensValidator,
    StartEndValidator,
    StringLengthValidator
  } from '../../../services/nameValidation';
import FlowManager from '../../common/FlowManager';
import { FILESPACE_MIN_ALLOWED_SYMBOLS } from '../../../utils/constants';
import { Message } from '../../common/Message';

interface ButtonState {
    text: string;
    isDisabled?: boolean;
}

interface FilespaceNameStepProperties {
    filespaces: string[];

    filespaceName: string;
    domainName: string;
    maxNameLength: number;

    onNameChanged: (name: string) => void;
    completeStep: () => void;

    errorMessage?: string | JSX.Element;

    buttonState: ButtonState;
    buttonProps?: any;
}

interface FilespaceNameStepState {
    hiddenFilespaceNameSpanText: string;
    placeholderLeft: number;
}

class FilespaceNameStep extends React.Component<FilespaceNameStepProperties, FilespaceNameStepState> {
    private flowManager: FlowManager;
    private requirementManager: NameRequirementManager;
    private hiddenSpanRef: React.RefObject<HTMLSpanElement> = React.createRef<HTMLSpanElement>();

    constructor(props: any) {
        super(props);

        this.handleUserInputChanged = this.handleUserInputChanged.bind(this);
        this.updatePlaceholderPosition = this.updatePlaceholderPosition.bind(this);
        this.getMaxNameLength = this.getMaxNameLength.bind(this);

        this.requirementManager = new NameRequirementManager();

        this.flowManager = new FlowManager(this.requirementManager.requirementsAreMet, this.props.completeStep);
        this.flowManager.registerListeners();

        const filespaceName = this.props.filespaceName ?? '';
        this.state = {
            hiddenFilespaceNameSpanText: filespaceName,
            placeholderLeft: 0
        };
    }

    componentDidUpdate(prevProps: FilespaceNameStepProperties) {
        if (this.props.filespaceName !== prevProps.filespaceName) {
            this.moveDomainNextToFilespaceName();
        }
    }

    componentWillMount() {
        this.setValidators();
    }

    componentDidMount() {
        this.moveDomainNextToFilespaceName();
    }

    componentWillUnmount() {
        this.flowManager.unregisterListeners();
    }

    render() {

        return (
            <div className="col-12 col-lg-6 col-xl-6 offset-lg-1 d-flex align-items-center input-step">
                <form>
                    {this.props.errorMessage && <Message isSuccess={false} message={this.props.errorMessage} />}

                    <div className="float-label-control nolabel large row">
                        <input
                            autoFocus={true}
                            value={this.props.filespaceName}
                            type="text"
                            className="form-control"
                            name="filespace"
                            id="file-space-name"
                            onChange={this.handleUserInputChanged}
                        />
                        <span
                            className="domainPlaceholder"
                            style={{ left: this.state.placeholderLeft }}
                        >
                            {'.' + this.props.domainName}
                        </span>
                        <span
                            className="hiddenSpan"
                            ref={this.hiddenSpanRef}
                        >
                            {this.state.hiddenFilespaceNameSpanText}
                        </span>
                        <label htmlFor="file-space-name">Filespace name</label>
                    </div>

                    {this.requirementManager.getRequirements(this.props.filespaceName)}
                    {this.renderButton()}
                </form>
            </div>
        );
    }

    private renderButton(): JSX.Element {
        const isDisabled =
            !this.requirementManager.requirementsAreMet() ||
            this.props.buttonState.isDisabled;
        return (
            <button
                id="myRenameButton"
                type="button"
                disabled={isDisabled}
                className="btn step-btn float-right"
                onClick={this.props.completeStep}
                {...this.props.buttonProps}
            >
                <span>{this.props.buttonState.text}</span>
                <RightArrowIcon width="13px" height="13px" />
            </button>
        );
    }

    private updatePlaceholderPosition(): void {
        if (!this.hiddenSpanRef.current) {
            return;
        }
        const placeholderLeft = this.hiddenSpanRef.current.offsetWidth;
        if (placeholderLeft !== undefined && this.state.placeholderLeft !== placeholderLeft) {
            this.setState({ placeholderLeft } );
        }
    }

    private handleUserInputChanged(e: any): void {
        const filespaceName = e.target.value.trim();
        this.props.onNameChanged(filespaceName);

        this.requirementManager.setEnabled(true);
    }

    private moveDomainNextToFilespaceName() {
        this.setState(
            { hiddenFilespaceNameSpanText: this.props.filespaceName },
            this.updatePlaceholderPosition
        );
    }

    private setValidators(): void {
        this.requirementManager.setValidators([
            new UniqueNameValidator(
                'The filespace name should be unique within your domain',
                this.props.filespaces
            ),
            new LowercaseLettersNumbersHyphensValidator(),
            new StartEndValidator(),
            new StringLengthValidator(
                FILESPACE_MIN_ALLOWED_SYMBOLS,
                this.getMaxNameLength()
            )
        ]);
    }

    private getMaxNameLength(): number {
            return this.props.maxNameLength - this.props.domainName.length;
    }
}

export { FilespaceNameStep, FilespaceNameStepProperties, FilespaceNameStepState };



// WEBPACK FOOTER //
// ./src/webportal/components/wizard/components/FilespaceNameStep.tsx